import classNames from "classnames";
import produce from "immer";
import noop from "lodash/noop";
import { FunctionComponent, useState } from "react";
import { useIntl } from "react-intl";
import ButtonSmily from "@Components/atoms/ButtonSmily";
import CartUpsells from "@Components/atoms/cart/CartUpsells";
import { useBreakpoint } from "@Hooks/useBreakpoint";
import useCartContext from "@Hooks/useCartContext";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import { ReactComponent as IconCopy } from "@Icons/copy.svg";
import { ReactComponent as IconEdit } from "@Icons/edit.svg";
import { ReactComponent as IconMinus } from "@Icons/minus.svg";
import { ReactComponent as IconMoreHorizontal } from "@Icons/more-horizontal.svg";
import { ReactComponent as IconPlus } from "@Icons/plus.svg";
import { ReactComponent as IconTrash } from "@Icons/trash.svg";
import CheerzApiCart from "@Types/api/cart";
import { ApiErrorResponse } from "@Types/api/error";
import { Article } from "@Types/cart";
import { convertCartFromApiToContent, convertCartFromContentToApi } from "@Utils/cart/cart";
import { errorManager } from "@Utils/error";
import { isProductEditable } from "@Utils/product";
import { convertToOrderUpdatedProperties, setDataLayerTrack } from "@Utils/track";
import CartArticleRemove from "./CartArticleRemove";

import "./style.scss";

interface Props {
  alternativeDesign: boolean;
  article: Article;
  disabled: boolean;
  onChangeQuantity: (modifiedArticle: Article) => void;
  onClickArticle?: (id: number) => void;
  onClickRemove?: () => void;
  onCopy: (locale: string, id: number) => void;
  onEdit: (modifiedArticle: Article) => void;
}

interface ContainerProps {
  alternativeDesign: boolean;
  article: Article;
  onClickArticle?: (id: number) => void;
  onClickRemove?: () => void;
  onCopy: (locale: string, id: number) => void;
}

export const CartArticle: FunctionComponent<Props> = ({
  alternativeDesign,
  article,
  disabled,
  onChangeQuantity,
  onClickArticle,
  onClickRemove,
  onCopy,
  onEdit,
}) => {
  const { isMediumScreen } = useBreakpoint();
  const intl = useIntl();
  const [displayRemovePanel, setDisplayRemovePanel] = useState<boolean>(false);

  const handleClickEdit = () =>
    isProductEditable(article.product) && (alternativeDesign || !isMediumScreen)
      ? onEdit(article)
      : noop;

  const CartArticleCounter = !!article.pagesCount && (
    <div className="CartArticle-content-description-info">
      <span>
        {article.pagesCount}&nbsp;
        {article.pagesCount > 1
          ? article.product.kustomPageNamePlural ||
            intl.formatMessage({ id: "cheerz_CartArticle_pages" })
          : article.product.kustomPageNameSingular ||
            intl.formatMessage({ id: "cheerz_CartArticle_page" })}
      </span>
      {!!article.photoCount && (
        <span>
          &nbsp;•&nbsp;{article.photoCount}&nbsp;
          {article.photoCount > 1
            ? intl.formatMessage({ id: "cheerz_CartArticle_photos" })
            : intl.formatMessage({ id: "cheerz_CartArticle_photo" })}
        </span>
      )}
    </div>
  );

  return (
    <div
      className={classNames("CartArticle", {
        "CartArticle--alternativeDesign": alternativeDesign,
        "CartArticle--editable": isProductEditable(article.product),
        "CartArticle--outOfStock": article.product.outOfStock,
      })}
      onClick={() => (!!onClickArticle ? onClickArticle(article.id) : noop)}
    >
      <div className="CartArticle-picture" onClick={handleClickEdit}>
        <div className="CartArticle-picture-overlay" />
        <div className="CartArticle-picture-alert">
          {intl.formatMessage({ id: "cheerz_CartArticle_outOfStock" })}
        </div>
        {article.customerImageUrl ? (
          <div
            className="CartArticle-picture-customerImage"
            style={{ backgroundImage: `url(${article.customerImageUrl})` }}
          />
        ) : (
          <img
            alt={article.product.name}
            className="CartArticle-picture-image"
            src={article.product.productImage.small}
          />
        )}
      </div>

      <div className="CartArticle-content">
        <div className="CartArticle-content-description">
          <div className="CartArticle-content-description-name" onClick={handleClickEdit}>
            {article.title ? article.title : article.product.name}{" "}
            {article.recovered && intl.formatMessage({ id: "cheerz_CartArticle_copied" })}
          </div>
          <div className="CartArticle-content-description-price">
            {intl.formatNumber(article.total.cents / 100, {
              currency: article.total.currency,
              style: "currency",
            })}
          </div>
        </div>

        {(alternativeDesign || !isMediumScreen) && (
          <div className="CartArticle-content-description">
            {article.title ? (
              <>
                <div className="CartArticle-content-description-info">{article.product.name}</div>
                {!!CartArticleCounter && (
                  <>
                    <div className="CartArticle-content-description-info">&nbsp;•&nbsp;</div>
                    {CartArticleCounter}
                  </>
                )}
              </>
            ) : (
              CartArticleCounter
            )}
          </div>
        )}

        <CartUpsells article={article} smallDesign={false} />

        {!article.product.outOfStock && isMediumScreen && !alternativeDesign && (
          <div className="CartArticle-content-quantity">
            {intl.formatMessage(
              { id: "cheerz_CartArticle_quantity" },
              {
                quantity: article.quantity,
              }
            )}
          </div>
        )}

        {(!isMediumScreen || alternativeDesign) && article.product.isQuantityUpdatable && (
          <div className="CartArticle-content-buttons">
            {!article.product.outOfStock && (
              <div className="CartArticle-content-buttons-left">
                <ButtonSmily
                  disabled={disabled}
                  icon={<IconMinus />}
                  iconPosition="alone"
                  onClick={() => {
                    article.quantity < 2
                      ? alternativeDesign && onClickRemove
                        ? onClickRemove()
                        : setDisplayRemovePanel(true)
                      : onChangeQuantity({
                          ...article,
                          quantity: article.quantity - 1,
                        });
                  }}
                  size="s"
                  type="tertiary"
                />
                <span className="CartArticle-content-buttons-quantity">{article.quantity}</span>
                <ButtonSmily
                  disabled={disabled || article.quantity > 499}
                  icon={<IconPlus />}
                  iconPosition="alone"
                  onClick={() =>
                    onChangeQuantity({
                      ...article,
                      quantity: article.quantity + 1,
                    })
                  }
                  size="s"
                  type="tertiary"
                />
              </div>
            )}
            {!alternativeDesign && (
              <div className="CartArticle-content-buttons-right">
                {isProductEditable(article.product) && (
                  <ButtonSmily
                    disabled={disabled}
                    expand={true}
                    icon={<IconEdit />}
                    iconPosition="right"
                    onClick={() => onEdit(article)}
                    size="s"
                    type="tertiary"
                  >
                    {intl.formatMessage({ id: "cheerz_CartArticle_button_edit" })}
                  </ButtonSmily>
                )}
                {!article.product.outOfStock && (
                  <ButtonSmily
                    disabled={disabled}
                    expand={true}
                    icon={<IconCopy />}
                    iconPosition="right"
                    onClick={() => onCopy(intl.locale, article.id)}
                    size="s"
                    type="tertiary"
                  >
                    {intl.formatMessage({ id: "cheerz_CartArticle_button_copy" })}
                  </ButtonSmily>
                )}
                <ButtonSmily
                  disabled={disabled}
                  expand={true}
                  icon={<IconTrash />}
                  iconPosition="right"
                  onClick={() => setDisplayRemovePanel(true)}
                  size="s"
                  type="tertiary"
                >
                  {intl.formatMessage({ id: "cheerz_CartArticle_button_delete" })}
                </ButtonSmily>
              </div>
            )}
          </div>
        )}
      </div>

      {alternativeDesign && (
        <div className="CartArticle-alternativeDesign-buttons">
          {isProductEditable(article.product) && (
            <ButtonSmily
              disabled={disabled}
              icon={<IconEdit />}
              iconPosition="left"
              onClick={() => onEdit(article)}
              size="m"
              type="primary"
            >
              {intl.formatMessage({ id: "cheerz_CartArticle_button_edit" })}
            </ButtonSmily>
          )}
          {!article.product.outOfStock && (
            <ButtonSmily
              disabled={disabled}
              icon={<IconCopy />}
              iconPosition="left"
              onClick={() => onCopy(intl.locale, article.id)}
              size="m"
              type="tertiary"
            >
              {intl.formatMessage({ id: "cheerz_CartArticle_button_copy" })}
            </ButtonSmily>
          )}
          <ButtonSmily
            disabled={disabled}
            icon={<IconTrash />}
            iconPosition="left"
            onClick={onClickRemove}
            size="m"
            type={article.product.outOfStock ? "primary" : "tertiary"}
          >
            {intl.formatMessage({ id: "cheerz_CartArticle_button_delete" })}
          </ButtonSmily>
        </div>
      )}

      <CartArticleRemove
        article={article}
        display={displayRemovePanel}
        onDismiss={() => setDisplayRemovePanel(false)}
        panel={true}
      />

      {isMediumScreen && !!onClickArticle && (
        <ButtonSmily
          disabled={disabled}
          icon={<IconMoreHorizontal />}
          iconPosition="alone"
          onClick={() => onClickArticle(article.id)}
          size="s"
          type="secondary"
        />
      )}
    </div>
  );
};

const CartArticleContainer: FunctionComponent<ContainerProps> = ({
  alternativeDesign,
  article,
  onClickArticle,
  onClickRemove,
  onCopy,
}) => {
  const { cart, configuration, dispatch, ui } = useCartContext();
  const { dispatchNotification } = useNotificationsContext();

  const handleChangeQuantity = (modifiedArticle: Article) => {
    dispatch({
      disabled: true,
      type: "setDisabledCart",
    });

    const updatedCart = produce(cart, (draftState) => {
      const updatedArticle = draftState.articles.find(
        (draftArticle) => draftArticle.id === modifiedArticle.id
      );

      if (updatedArticle) {
        updatedArticle.quantity = modifiedArticle.quantity;
      }
    });

    configuration.apiProviders.cheerz
      .saveCart(convertCartFromContentToApi(updatedCart))
      .then((cartResponse: CheerzApiCart) => {
        dispatch({
          cart: convertCartFromApiToContent(cartResponse),
          type: "setSavedCart",
        });
        setDataLayerTrack("Order Updated", convertToOrderUpdatedProperties(cart, "Checkout"));
      })
      .catch((error: ApiErrorResponse) => {
        errorManager(
          dispatchNotification,
          "CFE-001",
          error.response?.data?.error_description[0] ?? undefined
        );
        dispatch({
          disabled: false,
          type: "setDisabledCart",
        });
      });
  };

  const handleEdit = (modifiedArticle: Article) => {
    setDataLayerTrack("Cart Article Edit Clicked", {
      articleId: modifiedArticle.id,
      reorder: modifiedArticle.recovered ? "Reordered article" : "Not reordered article",
    });
    window.location.href = `customizations/${modifiedArticle.id}/edit`;
  };

  return (
    <CartArticle
      alternativeDesign={alternativeDesign}
      article={article}
      disabled={ui.disabled}
      onChangeQuantity={handleChangeQuantity}
      onClickArticle={onClickArticle}
      onClickRemove={onClickRemove}
      onCopy={onCopy}
      onEdit={handleEdit}
    />
  );
};

export default CartArticleContainer;
